import {
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  toggleItemWithSet,
  toggleAll
} from "collections/Table";
import { format } from "date-fns";
import {
  CancelButton,
  LoadingIndicator,
  NoResultsIndicator,
  SubmitButton
} from "elements";
import React, { useState } from "react";
import { SortState } from "store/filters";
import { useTranslation } from "utilities/i18n";
import { infiniteNestLink, Link, Route, Switch } from "polyrouter";
import { DefaultModalForm, useModalDispatch } from "collections/Modal";
import { DISMISS, useNotification } from "collections";
import {
  useDeletePendingInviteMutation,
  useInviteMemberMutation
} from "generated/graphql";
import { OperationContext } from "urql";
import { PendingInviteProp } from "routes/Admin/Accounts/Members/UserTable";

export const RemoveInvitesModal: React.FC<{
  invites: Array<PendingInviteProp>;
  removeIds: Array<string>;
  refresh: () => void;
}> = ({ invites, removeIds, refresh }) => {
  const { t } = useTranslation();
  const returnTo = useModalDispatch();
  const [, deleteInvite] = useDeletePendingInviteMutation();
  const [addNotification] = useNotification();
  removeIds = removeIds.filter(id => id !== HEADER_ID);
  const emailsToRemove = removeIds.reduce((emails, id) => {
    const inviteObject = invites.find(invite => invite.id === id);
    return inviteObject ? [...emails, inviteObject.email] : emails;
  }, [] as Array<string>);

  const removeInvites = async () => {
    let noError = true;

    for (let index = 0; index < removeIds.length; index++) {
      const resp = await deleteInvite({
        inviteId: removeIds[index]
      });
      if (resp.error) {
        noError = false;
        addNotification({
          type: "error",
          id: "delete-invite-error",
          message: t("ERROR_DELETING_PENDING_INVITE"),
          dismiss: DISMISS.DEFAULT,
          persistThroughNav: true
        });
      }
    }

    if (noError) {
      refresh();
      invites.filter(invite => !removeIds.includes(invite.id));
      addNotification({
        type: "success",
        id: "delete-invites-success",
        message: t("SUCCESSFULLY_DELETED_PENDING_INVITES"),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    }
    returnTo();
  };

  return (
    <DefaultModalForm
      title={t("DELETE_PENDING_INVITATION", { count: removeIds.length })}
      onSubmit={removeInvites}
      name="RemoveInvite"
    >
      {({ isSubmitting }) => (
        <>
          <span data-testid="confirmation">
            {t("CONFIRM_DELETE_INVITATION", { count: removeIds.length })}
          </span>
          <div className="small-text overflow-list modal-body pt-1 pl-1">
            {emailsToRemove.map((email, i) => (
              <p key={i} className="list-item">
                {email}
              </p>
            ))}
          </div>
          <div className="btn-container">
            <CancelButton onClick={returnTo} disabled={isSubmitting} />
            <SubmitButton
              isSubmitting={isSubmitting}
              testId="remove"
              labelKey="DELETE_INVITATION"
              labelKeyData={{ count: removeIds.length }}
              submittingLabelKey="DELETING"
            />
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};

type RoleBasedInvites = {
  role: string;
  emails: Array<string>;
};
export const ResendInvitesModal: React.FC<{
  invites: Array<PendingInviteProp>;
  resendIds: Array<string>;
  tenantId: string;
  setSelectedItems: (value: React.SetStateAction<string[]>) => void;
}> = ({ invites, resendIds, tenantId, setSelectedItems }) => {
  const { t } = useTranslation();
  const returnTo = useModalDispatch();
  const [addNotification] = useNotification();
  const [, sendInvite] = useInviteMemberMutation();
  resendIds = resendIds.filter(id => id !== HEADER_ID);
  let roleBasedInvitesList = [] as Array<RoleBasedInvites>;
  let resendingEmails = [] as Array<string>;
  for (let index = 0; index < resendIds.length; index++) {
    const inviteObject = invites.find(invite => invite.id === resendIds[index]);
    if (inviteObject) {
      resendingEmails.push(inviteObject.email);
      const emails = roleBasedInvitesList.find(
        obj => obj.role === inviteObject.role_name
      )?.emails;
      if (emails) {
        emails.push(inviteObject.email);
      } else {
        roleBasedInvitesList.push({
          role: inviteObject.role_name,
          emails: [inviteObject.email]
        });
      }
    }
  }

  const resendInvites = async () => {
    let noError = true;

    for (let index = 0; index < roleBasedInvitesList.length; index++) {
      const inviteRes = await sendInvite({
        emails: roleBasedInvitesList[index].emails,
        tenant_id: tenantId,
        role: roleBasedInvitesList[index].role
      });
      if (inviteRes.error) {
        noError = false;
        addNotification({
          type: "error",
          id: "delete-invite-error",
          message: t("INVITATION_ERROR"),
          dismiss: DISMISS.DEFAULT,
          persistThroughNav: true
        });
      }
    }

    if (noError) {
      addNotification({
        type: "success",
        id: "delete-invites-success",
        message: t("EMAIL_SENT", { count: resendingEmails.length }),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    }
    setSelectedItems([]);
    returnTo();
  };

  return (
    <DefaultModalForm
      title={t("RESEND_INVITATION", { count: resendIds.length })}
      onSubmit={resendInvites}
      name="RemoveInvite"
    >
      {({ isSubmitting }) => (
        <>
          <span data-testid="confirmation">
            {t("CONFIRM_RESEND_INVITATION", { count: resendIds.length })}
          </span>
          <div className="small-text overflow-list modal-body pt-1 pl-1">
            {resendingEmails.map((email, i) => (
              <p key={i} className="list-item">
                {email}
              </p>
            ))}
          </div>
          <div className="btn-container">
            <CancelButton onClick={returnTo} />
            <SubmitButton
              isSubmitting={isSubmitting}
              testId="resend"
              labelKey="RESEND_INVITATION"
              labelKeyData={{ count: resendingEmails.length }}
              submittingLabelKey="SENDING"
            />
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};

export const PendingInvitsListView: React.FC<{
  tenantId: string;
  pendingInvitesList: Array<PendingInviteProp> | undefined;
  fetching: boolean;
  stale: boolean;
  memberCanEdit: boolean;
  executor: (opts?: Partial<OperationContext> | undefined) => void;
}> = ({
  tenantId,
  pendingInvitesList,
  fetching,
  stale,
  memberCanEdit,
  executor
}) => {
  const { t } = useTranslation();
  const [currentSort] = useState<SortState>({
    field: "email",
    direction: "ASC"
  });
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);

  const refresh = () => {
    setSelectedItems([]);
    executor({
      requestPolicy: "network-only"
    });
  };

  const columns = [
    { key: "email", label: t("EMAIL") },
    { key: "date-invited", label: t("DATE_OF_INITIAL_INVITE") },
    { key: "role", label: t("ROLE") }
  ];

  if (!pendingInvitesList || pendingInvitesList.length === 0) return null;
  sortTableData(pendingInvitesList, currentSort);

  const getFormattedDate = (date: string) => {
    return format(new Date(date), "MM/d/yyyy");
  };

  const toggleItem = toggleItemWithSet(
    pendingInvitesList.length,
    selectedItems,
    setSelectedItems
  );
  const deleteButton =
    selectedItems.length > 0 ? (
      <Link
        to={infiniteNestLink("/remove-invitation")}
        className="btn"
        data-testid="remove-invitation"
      >
        {t("REMOVE")}
      </Link>
    ) : (
      <button className="btn disabled" data-testid="remove-invitation" disabled>
        {t("REMOVE")}
      </button>
    );
  const resendButton =
    selectedItems.length > 0 ? (
      <Link
        to={infiniteNestLink("/resend-invitation")}
        className="btn btn-primary"
        data-testid="resend-invitation"
      >
        {t("RESEND_INVITATION")}
      </Link>
    ) : (
      <button className="btn disabled" data-testid="resend-invitation" disabled>
        {t("RESEND_INVITATION")}
      </button>
    );

  const itemChecked = itemCheckedWithSet(selectedItems);

  return (
    <>
      <div className="grouping table-actions mb-1">
        {memberCanEdit && (
          <>
            {resendButton}
            {deleteButton}
          </>
        )}
      </div>
      <div className="table-container">
        <table
          className="table custom-table"
          data-testid="pending-invites-table"
        >
          <TableBulkItemHeader
            showChecks={memberCanEdit && !!pendingInvitesList.length}
            checked={itemChecked(HEADER_ID)}
            onCheck={() =>
              toggleAll(
                pendingInvitesList.map(invite => invite.id),
                selectedItems,
                setSelectedItems
              )
            }
          >
            {columns.map(({ key, label }) => (
              <TableHeaderItem fieldName={key} title={label} key={key} />
            ))}
          </TableBulkItemHeader>
          <TableBody loading={false} cols={columns.length} accommodateScrollbar>
            {pendingInvitesList?.map((invite, index) => (
              <TableBulkItemRow
                showChecks={memberCanEdit && !!pendingInvitesList.length}
                checked={itemChecked(invite.id)}
                onCheck={() => toggleItem(invite.id)}
                key={invite.id}
                data-testid={`invite-${index}`}
              >
                <td>{invite.email}</td>
                <td>{getFormattedDate(invite.created_at)}</td>
                <td>{t(invite.role_name)}</td>
              </TableBulkItemRow>
            ))}
          </TableBody>
        </table>
        {!fetching && pendingInvitesList?.length === 0 ? (
          <NoResultsIndicator />
        ) : null}
        {stale || fetching ? <LoadingIndicator key="loading" /> : null}
      </div>
      <Switch>
        <Route nest path="/resend-invitation">
          <ResendInvitesModal
            invites={pendingInvitesList}
            resendIds={selectedItems}
            tenantId={tenantId}
            setSelectedItems={setSelectedItems}
          />
        </Route>
        <Route
          nest
          path="/remove-invitation"
          render={() => (
            <RemoveInvitesModal
              invites={pendingInvitesList}
              removeIds={selectedItems}
              refresh={refresh}
            />
          )}
        />
      </Switch>
    </>
  );
};
